import { CoreDataSchemaReference, DataDisplayOptions, DataFormat, DataSchemaReference, DataSchemaType, DataSourceDto, DataSourceReference, DataSourceType, ProductDataSourceReference, ResultSetDataSourceReference, UserDatasetDataSourceReference, ValueDataType, VisualCustomFieldsDataSchemaReference } from 'src/app/services/api.services';
import { deepClone } from 'src/app/shared/clone/cloner-service';
import { getProductDescriptor } from 'src/app/shared/utils';
import { ElementCollection, NamedElementCollection } from './Collections';
import { DataModel } from './DataModel';
import { DataSchema } from './DataSchema';
import { DataTable } from './DataTable';
import { VisualCustomField } from '../visuals/VisualCustomField';
import { mapToRecord } from '../utils/mapToRecord';

export class DataSource {
  readonly tables = new DataSource.TableCollection(this);
  readonly schemas = new DataSource.SchemaCollection(this);
  readonly fields = new DataSource.FieldLookup(this);
  
  readonly referenceString: string;

  constructor(
    public readonly model: DataModel,
    public readonly reference: DataSourceReference) {
    this.referenceString =
      this.reference.type == DataSourceType.Product
        ? `${this.product.name}`
        :
      this.reference.type == DataSourceType.UserDataset
        ? `UserDataset#${this.userDatasetId}`
        :
      this.reference.type == DataSourceType.ResultSet
        ? `ResultSet#${this.resultSetId}`
        : `undefined`;
  }

  get type() { return this.reference.type; }

  get isProduct() { return this.type == DataSourceType.Product; }

  readonly product = this.isProduct
      ? getProductDescriptor((this.reference as ProductDataSourceReference).product)
      : null;

  get isResultSet() { return this.type == DataSourceType.ResultSet; }

  readonly resultSetId =
    this.isResultSet
      ? (this.reference as ResultSetDataSourceReference).resultSetId
      : null;

  get isUserDataset() { return this.type == DataSourceType.UserDataset; }

  readonly userDatasetId =
    this.isUserDataset
        ? (this.reference as UserDatasetDataSourceReference).userDatasetId
        : null;

  readonly displayOrder = DataSource.getDefaultDisplayOrder(this);

  readonly defaultFormats = new Map<ValueDataType, DataFormat>();
  readonly defaultDisplayOptions = new Map<ValueDataType, DataDisplayOptions>();

  readonly additionalProperties = new Map<string, any>();


  setFromDto1(dto: DataSourceDto) {
    for (let [k, v] of Object.entries(dto.defaultFormats)) {
      this.defaultFormats.set(ValueDataType[k], deepClone(v));
    }

    for (let [k, v] of Object.entries(dto.defaultDisplayOptions)) {
      this.defaultDisplayOptions.set(ValueDataType[k], deepClone(v));
    }

    for (let [k, v] of Object.entries(dto.additionalProperties)) {
      this.additionalProperties.set(k, deepClone(v));
    }

    dto.tables.forEach(x => this.tables.add(x.name).setFromDto1(x));
  }

  setFromDto2(dto: DataSourceDto) {
    dto.tables.forEach(x => this.tables.get(x.name).setFromDto2(x));

    dto.schemas.forEach(x => this.schemas.add(x.reference).setFromDto(x));
  }

  toDto(): DataSourceDto {
    return {
      reference: this.reference,

      tables: [...this.tables.map(x => x.toDto())],
      schemas: [...this.schemas.map(x => x.toDto())],

      defaultFormats: mapToRecord(this.defaultFormats, x => ValueDataType[x]),
      defaultDisplayOptions: mapToRecord(this.defaultDisplayOptions, x => ValueDataType[x]),

      additionalProperties: mapToRecord(this.additionalProperties),
    };
  }
}

export namespace DataSource {
  export function getDefaultDisplayOrder(ds: DataSource) {
    if (ds.isProduct) {
      return `0_${ds.product.order}`;
    }

    if (ds.isUserDataset) {
      return `1_${ds.userDatasetId}`;
    }

    if (ds.isResultSet) {
      return `2_${ds.resultSetId}`;
    }

    return `3_${ds.referenceString}`;
  }

  export class TableCollection extends NamedElementCollection<DataTable> {
    constructor(public readonly s: DataSource) {
      super();
    }

    add(name: string) {
      let item = new DataTable(this.s, { source: this.s.reference, table: name });
      this._add(item);
      return item;
    }

    protected getBase() { return this.s.model.base?.sources.find(this.s.reference)?.tables; }
    protected getExtensions() { return this.s.model.base?.extensions.map(x => x.sources.find(this.s.reference)?.tables) ?? []; }
  }

  export class SchemaCollection extends ElementCollection<DataSchemaReference, DataSchema> {
    constructor(public readonly s: DataSource) {
      super();
    }

    add(reference: DataSchemaReference) {
      let item = new DataSchema(this.s, reference);
      this._add(item);
      return item;
    }

    addCore(name: string): DataSchema {
      return this.add(<CoreDataSchemaReference>{
        type: DataSchemaType.Core,
        name
      });
    }

    getOrAddCore(name: string): DataSchema {
      return this.find({
        type: DataSchemaType.Core,
        name
      }) ?? this.addCore(name);
    }

    addVisualCustomFields(schema: DataSchemaReference, visualId: number): DataSchema {
      return this.add(<VisualCustomFieldsDataSchemaReference> {
        type: DataSchemaType.VisualCustomFields,
        name: VisualCustomField.GROUP_NAME,
        schema,
        visualId
      });
    }

    getOrAddVisualCustomFields(schema: DataSchemaReference, visualId: number): DataSchema {
      return this.find(<VisualCustomFieldsDataSchemaReference>{
        type: DataSchemaType.VisualCustomFields,
        name: VisualCustomField.GROUP_NAME,
        schema,
        visualId,
      }) ?? this.addVisualCustomFields(schema, visualId);
    }

    protected normalizeKey(key: DataSchemaReference) {
      switch (key.type) {
        case DataSchemaType.Core:
          return `Core/${key.name.toLowerCase()}`;

        case DataSchemaType.VisualCustomFields: {
          let x = <VisualCustomFieldsDataSchemaReference>key;
          return `${this.normalizeKey(x.schema)}/VisualCustomFields/${x.visualId}/${key.name.toLowerCase()}`;
        }
      }

      throw new Error(`Unsupported DataSchemaReference`, { cause: key });
    }

    protected getKey(item: DataSchema) { return item.reference; }

    protected getBase() { return this.s.model.base?.sources.find(this.s.reference)?.schemas; }
    protected getExtensions() { return this.s.model.base?.extensions.map(x => x.sources.find(this.s.reference)?.schemas) ?? []; }
  }

  export class FieldLookup {
    constructor(public readonly s: DataSource) { }

    find(table: string, ...path: string[]) {
      return this.s.tables.find(table)?.fields.find(...path);
    }

    get(table: string, ...path: string[]) {
      let item = this.find(table, ...path);
      if (!item)
        throw new Error(`Could not find field [${table}].[${path.join('].[')}]`);

      return item;
    }
  }
}
